import React, { useEffect, useRef, useState } from 'react'
import { parseISO, format } from 'date-fns'
import toastr from 'toastr'
import { SiGooglecalendar } from 'react-icons/si'
import { FaMicrosoft, FaApple } from 'react-icons/fa6'
import {
  generateGoogleCalendarURL,
  generateOutlookCalendarURL,
  generateICSContent,
  downloadICSFile,
} from '../../utils/calendarIntegration'
import { services } from '../../services'
import { MATCH_STATUS, isCompletedStatus } from '../../utils/matchStatus'
import { getMatchOutcomeForPlayer, getHomeAwayStatus } from '../../utils/match'

/**
 * MatchCard
 *
 * Props:
 * - match: match object with fields used below
 * - onClickMatch(match): called when the card is clicked
 * - onCancelMatch(match, event): called when cancel match is clicked
 * - currentPlayerId: number used to compute opponent name (optional)
 * - calendarEnabled: boolean to show/hide calendar dropdown (default true)
 * - showSeeds: boolean to display player seeds (default false, auto-enabled for playoff matches)
 * - showWinner: boolean to display winner indication for completed matches (default true)
 */
export default function MatchCard({
  match,
  onClickMatch,
  onCancelMatch,
  currentPlayerId,
  calendarEnabled = true,
  leagueName,
  divisionName,
  showLeagueDivision = true,
  showSeeds = false,
  showWinner = true,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const getOpponentName = (m) => {
    const viewerId = currentPlayerId ?? m?.current_player_id
    try {
      return services.match.getOpponentName(m, viewerId) || 'Opponent'
    } catch {
      return 'Opponent'
    }
  }

  const getHomeCourtInfo = (m) => {
    if (!m?.home_court_name) return null
    const courtName = m.home_court_name
    const location = [m.home_court_city, m.home_court_state]
      .filter(Boolean)
      .join(', ')
    return { courtName, location }
  }

  const formatDateTime = (datetime) => {
    if (!datetime) return 'TBD'
    try {
      const dateObj =
        typeof datetime === 'string' ? parseISO(datetime) : new Date(datetime)
      return format(dateObj, 'EEE, MMM d, yyyy • h:mm a')
    } catch {
      return String(datetime)
    }
  }

  const handleAddToGoogleCalendar = (m, e) => {
    e.stopPropagation()
    const options = {
      leagueName,
      divisionName,
      opponentName: getOpponentName(m),
    }
    const url = generateGoogleCalendarURL(m, options)
    if (url) window.open(url, '_blank')
    else toastr.warning('Match datetime is not set')
  }

  const handleAddToOutlookCalendar = (m, e) => {
    e.stopPropagation()
    const options = {
      leagueName,
      divisionName,
      opponentName: getOpponentName(m),
    }
    const url = generateOutlookCalendarURL(m, options)
    if (url) window.open(url, '_blank')
    else toastr.warning('Match datetime is not set')
  }

  const handleDownloadICS = (m, e) => {
    e.stopPropagation()
    const opponentName = getOpponentName(m)
    const options = {
      leagueName,
      divisionName,
      opponentName,
    }
    const icsContent = generateICSContent(m, options)
    if (icsContent) {
      const filename = `tennis_match_${opponentName.replace(
        /[^a-zA-Z0-9]/g,
        '_',
      )}.ics`
      downloadICSFile(icsContent, filename)
      toastr.success('Calendar event downloaded')
    } else {
      toastr.warning('Match datetime is not set')
    }
  }

  const opponentName = getOpponentName(match)
  const homeCourt = getHomeCourtInfo(match)
  const displayLeagueName = leagueName || match.league_name
  const displayDivisionName = divisionName || match.division_name

  // Determine if this is a playoff match or if seeds should be shown
  const isPlayoffMatch = match?.type === 'playoff'
  const shouldShowSeeds = showSeeds || isPlayoffMatch

  // Get match completion status and winner
  const isCompleted = isCompletedStatus(match?.status)
  const matchOutcome =
    showWinner && isCompleted && currentPlayerId
      ? getMatchOutcomeForPlayer(match, currentPlayerId)
      : null

  // Get status badge info
  const getStatusInfo = () => {
    if (isCompleted) {
      // Completed matches
      if (match.status === MATCH_STATUS.NORMAL) {
        return { label: 'Completed', className: 'completed', icon: '✅' }
      } else {
        // Retirement/walkover
        return { label: 'Walkover', className: 'walkover', icon: '⚠️' }
      }
    } else if (match.status === MATCH_STATUS.SCHEDULED) {
      // Don't show badge for scheduled matches - datetime field is sufficient
      return null
    } else if (match.status === MATCH_STATUS.CREATED) {
      return { label: 'Unscheduled', className: 'unscheduled', icon: '📝' }
    }
    return { label: 'Pending', className: 'pending', icon: '⏳' }
  }

  const statusInfo = getStatusInfo()

  // Helper to get seed display for a player
  // Seeds are stored in home_seed/away_seed columns
  const getPlayerSeed = (isHome) => {
    if (isHome) {
      return match?.home_seed ?? null
    }
    return match?.away_seed ?? null
  }

  const viewerId = currentPlayerId ?? match?.current_player_id
  const homeAwayStatus = getHomeAwayStatus(match, viewerId)
  const isViewerHome = homeAwayStatus.isHome
  const opponentSeed = shouldShowSeeds ? getPlayerSeed(!isViewerHome) : null
  const homeAwayText = homeAwayStatus.text.toUpperCase()

  // Build CSS class names for match card
  const getMatchCardClasses = () => {
    const classes = ['match-card']
    if (isCompleted) classes.push('match-completed')
    if (matchOutcome === 'win') classes.push('match-won')
    else if (matchOutcome === 'loss') classes.push('match-lost')
    return classes.join(' ')
  }

  return (
    <div
      className={getMatchCardClasses()}
      onClick={() => onClickMatch && onClickMatch(match)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClickMatch && onClickMatch(match)
      }}
    >
      <div className="match-header">
        <div className="opponent-info">
          <h4 className="opponent-name">
            {homeAwayText && `${homeAwayText} vs `}
            {opponentName}
            {opponentSeed && (
              <span className="player-seed">#{opponentSeed}</span>
            )}
          </h4>
          {showLeagueDivision && (
            <div className="match-details">
              <span className="league-info">
                {displayLeagueName} • {displayDivisionName}
              </span>
            </div>
          )}
          {matchOutcome && (
            <div className="match-outcome">
              <span className={`outcome-badge ${matchOutcome}`}>
                {matchOutcome === 'win' ? '🏆 Victory' : '📉 Loss'}
              </span>
            </div>
          )}
        </div>
        {statusInfo && (
          <div className="match-status">
            <span className={`status-badge ${statusInfo.className}`}>
              <span aria-hidden="true">{statusInfo.icon}</span>{' '}
              {statusInfo.label}
            </span>
          </div>
        )}
      </div>

      <div className="match-info">
        <div className="datetime-info">
          <span className="datetime">
            <span aria-hidden="true">📅</span>{' '}
            {formatDateTime(match.match_datetime)}
          </span>
          {homeCourt && (
            <div className="court-info">
              <span className="court-name">
                <span aria-hidden="true">🎾</span>{' '}
                <strong>Match Location:</strong> {homeCourt.courtName}
              </span>
              {homeCourt.location && (
                <span className="court-location">
                  <span aria-hidden="true">📍</span> {homeCourt.location}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="match-actions">
          <button
            className="ll-btn ll-btn-primary ll-btn-action-primary"
            onClick={(e) => {
              e.stopPropagation()
              onClickMatch && onClickMatch(match)
            }}
            title="Enter match score"
            data-track="match.enter_score_from_scheduled"
          >
            <span aria-hidden="true">✏️</span> Enter Score
          </button>

          <button
            className="ll-btn ll-btn-secondary-action"
            onClick={(e) => onCancelMatch && onCancelMatch(match, e)}
            title="Cancel match"
            aria-label="Cancel match"
          >
            <span aria-hidden="true">🗑️</span>
            <span className="d-none d-md-inline ms-1">Cancel</span>
          </button>

          {calendarEnabled && match.match_datetime && (
            <div className="dropup d-inline" ref={dropdownRef}>
              <button
                className="ll-btn ll-btn-secondary-action dropdown-toggle"
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setDropdownOpen((v) => !v)
                }}
                aria-expanded={dropdownOpen}
                aria-label="Add to calendar"
                title="Add to calendar"
              >
                <span aria-hidden="true">📅</span>
                <span className="d-none d-md-inline ms-1">Calendar</span>
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={(e) => handleAddToGoogleCalendar(match, e)}
                  >
                    <SiGooglecalendar aria-hidden="true" />
                    <span>Google Calendar</span>
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={(e) => handleAddToOutlookCalendar(match, e)}
                  >
                    <FaMicrosoft aria-hidden="true" />
                    <span>Outlook Calendar</span>
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={(e) => handleDownloadICS(match, e)}
                  >
                    <FaApple aria-hidden="true" />
                    <span>Apple Calendar (.ics)</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
